import { computed } from 'vue'
import { resolveModules, resolveAvailableTours } from '../registry/tour-registry'
import { useTourProgressStore } from '../stores/tour-progress.store'
import { buildTourContext } from './useTourContext'

export interface ModuleProgress {
  module: string
  total: number
  completed: number
  percent: number
  tours: { id: string; title: string; routeName: string; completed: boolean }[]
}

/**
 * Derives per-screen, per-module and total training progress from the registry +
 * persisted store. All derived via `computed` — nothing recomputes while idle.
 */
export function useTourProgress() {
  const progress = useTourProgressStore()

  const available = computed(() => resolveAvailableTours(buildTourContext()))

  const totalTours = computed(() => available.value.length)
  const completedTours = computed(
    () => available.value.filter((t) => progress.isCompleted(t.id)).length,
  )
  const totalPercent = computed(() =>
    totalTours.value === 0 ? 0 : Math.round((completedTours.value / totalTours.value) * 100),
  )
  const allComplete = computed(
    () => totalTours.value > 0 && completedTours.value === totalTours.value,
  )

  const modules = computed<ModuleProgress[]>(() =>
    resolveModules(buildTourContext()).map(({ module, tours }) => {
      const withState = tours.map((t) => ({
        id: t.id,
        title: t.title,
        routeName: t.routeName,
        completed: progress.isCompleted(t.id),
      }))
      const completed = withState.filter((t) => t.completed).length
      return {
        module,
        total: withState.length,
        completed,
        percent: withState.length === 0 ? 0 : Math.round((completed / withState.length) * 100),
        tours: withState,
      }
    }),
  )

  return { totalTours, completedTours, totalPercent, allComplete, modules }
}
